<template>
    <div class="grafico-container">
        <div class="grafico-header">
            <h3 class="grafico-titulo">Conformidade de Prazo por Projeto</h3>
            <GraficoInfoIcon 
                titulo="Conformidade de Prazo por Projeto"
                descricao="Mostra o percentual de tarefas concluídas por projeto, indicando a conformidade com os prazos estabelecidos."
            />
        </div>
        <div v-if="!temDados" class="sem-dados">Nenhum dado disponível</div>
        <Bar v-else :data="chartData" :options="chartOptions" />
    </div>
</template>

<script>
import { Bar } from 'vue-chartjs';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import GraficoInfoIcon from './GraficoInfoIcon.vue';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default {
    name: 'GraficoConformidadePrazo',
    components: { Bar, GraficoInfoIcon },
    props: {
        projetos: {
            type: Array,
            default: () => []
        }
    },
    emits: ['clicar-projeto'],
    methods: {
        formatarNomeProjeto(projeto, maxLength = 25) {
            const nome = projeto.nome || 'Sem nome';
            const codigoPCM = projeto.pcm?.codigo || '';
            const nomeCompleto = codigoPCM ? `${codigoPCM} - ${nome}` : nome;
            return nomeCompleto.length > maxLength ? nomeCompleto.substring(0, maxLength) + '...' : nomeCompleto;
        }
    },
    computed: {
        projetosComDados() {
            return this.projetos
                .filter(p => (p.estatisticas?.tarefas?.total ?? 0) > 0)
                .sort((a, b) => (b.estatisticas?.tarefas?.porcentagem_concluidas ?? 0) - (a.estatisticas?.tarefas?.porcentagem_concluidas ?? 0))
                .slice(0, 50);
        },
        temDados() {
            return this.projetosComDados.length > 0;
        },
        chartData() {
            const projetos = this.projetosComDados;
            return {
                labels: projetos.map(p => this.formatarNomeProjeto(p)),
                datasets: [{
                    label: 'Conformidade (%)',
                    data: projetos.map(p => p.estatisticas?.tarefas?.porcentagem_concluidas ?? 0),
                    backgroundColor: projetos.map(p => {
                        const porcentagem = p.estatisticas?.tarefas?.porcentagem_concluidas ?? 0;
                        if (porcentagem >= 80) return 'rgba(46, 204, 113, 0.6)';
                        if (porcentagem >= 50) return 'rgba(52, 152, 219, 0.6)';
                        if (porcentagem >= 30) return 'rgba(241, 196, 15, 0.6)';
                        return 'rgba(231, 76, 60, 0.6)';
                    }),
                    borderColor: projetos.map(p => {
                        const porcentagem = p.estatisticas?.tarefas?.porcentagem_concluidas ?? 0;
                        if (porcentagem >= 80) return 'rgba(46, 204, 113, 1)';
                        if (porcentagem >= 50) return 'rgba(52, 152, 219, 1)';
                        if (porcentagem >= 30) return 'rgba(241, 196, 15, 1)';
                        return 'rgba(231, 76, 60, 1)';
                    }),
                    borderWidth: 2
                }]
            };
        },
        chartOptions() {
            const self = this;
            return {
                responsive: true,
                maintainAspectRatio: false,
                onClick: (event, elements) => {
                    if (elements.length > 0) {
                        const index = elements[0].index;
                        const projeto = this.projetosComDados[index];
                        if (projeto) {
                            self.$emit('clicar-projeto', projeto.id);
                        }
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        max: 100,
                        title: {
                            display: true,
                            text: 'Conformidade (%)'
                        },
                        ticks: {
                            callback: (value) => value + '%'
                        }
                    },
                    x: {
                        title: {
                            display: true,
                            text: 'Projetos'
                        }
                    }
                },
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const projeto = this.projetosComDados[context.dataIndex];
                                const total = projeto?.estatisticas?.tarefas?.total ?? 0;
                                const concluidas = projeto?.estatisticas?.tarefas?.concluidas ?? 0;
                                return [
                                    `Conformidade: ${context.parsed.y.toFixed(1)}%`,
                                    `Tarefas: ${concluidas}/${total}`
                                ];
                            },
                            footer: () => 'Clique para ver detalhes'
                        }
                    }
                }
            };
        }
    }
};
</script>

<style scoped>
.grafico-container {
    background: var(--cor-bg, white);
    border: 1px solid var(--cor-separador, #ddd);
    border-radius: 8px;
    padding: 1.5rem;
    height: 700px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
}

.grafico-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.grafico-titulo {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--cor-fonte, #333);
}

.sem-dados {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
}
</style>

